import { isPlatformBrowser, NgTemplateOutlet } from '@angular/common';
import { Component, ElementRef, HostBinding, HostListener, input, OnDestroy, PLATFORM_ID, viewChild, inject, output, signal, effect, untracked, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { MarkdownComponent, provideMarkdown } from 'ngx-markdown';
import { lastValueFrom, Subject, takeUntil } from 'rxjs';

import { WeatherComponent } from '@components/weather/weather.component';

import IPixelMapDay, { IPixelStatusKey } from '@interfaces/pixel-map-day.interface';

import { AccentService } from '@services/accent-service.service';
import { AlbumArtService } from '@services/album-art.service';
import { CalendarDataService } from '@services/calendar-data.service';

import { SafeUrlPipe } from '@utils/pipes/safe-resource-url.pipe';

@Component({
	selector: 'app-calendar-entry',
	imports: [
		MarkdownComponent,
		WeatherComponent,
		NgTemplateOutlet,
		SafeUrlPipe,
		RouterLink,
	],
	providers: [provideMarkdown()],
	templateUrl: './calendar-entry.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	styleUrls: [
		'./calendar-entry.component.scss',
		'./calendar-entry-themes.scss'
	],
})
export class CalendarEntryComponent implements OnDestroy {
	private accent = inject(AccentService);
	private calendarDataService = inject(CalendarDataService);
	private albumArtService = inject(AlbumArtService);
	private router = inject(Router);
	private destroy$ = new Subject<void>();
	private readonly albumArtCache = new Map<string, string>(); // key -> url ('' means none)
	private readonly albumArtInFlight = new Map<string, Promise<string | null>>();

	private platformId = inject<Object>(PLATFORM_ID);
	isBrowser: boolean = isPlatformBrowser(this.platformId);

	private modal = viewChild<ElementRef<HTMLElement>>('calendarModal');

	year = input.required<number>();
	month = input.required<number>();
	day = input.required<number>();
	dayData = input.required<IPixelMapDay>();
	isStandalone = input<boolean>(false);
	extension = input<"shuffle" | undefined>(undefined);
	
	// FLIP animation inputs
	modalState = input<'closed' | 'opening' | 'open' | 'closing' | undefined>();
	modalOrigin = input<{top: number, left: number, width: number, height: number} | null | undefined>();

	private el = inject(ElementRef);
	private cdr = inject(ChangeDetectorRef);

	private setStyles(styles: Partial<CSSStyleDeclaration>) {
		if (!this.el?.nativeElement || !this.isStandalone()) return;
		Object.assign(this.el.nativeElement.style, styles);
	}

	ngOnInit() {
		if (this.isStandalone()) {
			this.setStyles({ transformOrigin: 'top left' });
		}
	}

	private applyFlipOrigin() {
		const origin = this.modalOrigin();
		if (!origin || !this.el?.nativeElement) return;
		
		// The modal's natural position in the center of the screen
		const destRect = this.el.nativeElement.getBoundingClientRect();
		const destTop = destRect.top + window.scrollY;
		const destLeft = destRect.left + window.scrollX;
		
		const dx = origin.left - destLeft;
		const dy = origin.top - destTop;
		const sx = origin.width / destRect.width;
		const sy = origin.height / destRect.height;

		this.setStyles({ transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})` });
	}

	readonly emitButtonAction = output<"prev" | "next">();
	readonly closeButtonAction = output<"close">();
	readonly shuffleButtonAction = output<"shuffle">();

	calendarEntry: IPixelMapDay | null = null;
	readonly spotifyImage = signal<string | null>(null);

	calendarMonths = this.calendarDataService.calendarMonths;
	activeDayName: string = "Fri";
	copyLinkText = "Copy link";
	canWebShare = false;

	prevLink = input<any[]>([]);
	nextLink = input<any[]>([]);

	moodIcons: Record<IPixelStatusKey, string> = {
		"special": "add_reaction",
		"excellent": "sentiment_very_satisfied",
		"good": "mood",
		"above-average": "sentiment_satisfied",
		"average": "sentiment_neutral",
		"below-average": "sentiment_dissatisfied",
		"bad": "sentiment_very_dissatisfied",
		"awful": "sentiment_extremely_dissatisfied"
	};

	moodText: Record<IPixelStatusKey, string> = {
		"special": "Special",
		"excellent": "Excellent",
		"good": "Good",
		"above-average": "Above Average",
		"average": "Average",
		"below-average": "Below Average",
		"bad": "Bad",
		"awful": "Awful"
	};

	get openReaderLink(): string {
		const m = String(this.month() + 1).padStart(2, "0");
		const d = String(this.day() + 1).padStart(2, "0");
		return `/calendar/${this.year()}/${m}/${d}`;
	}

	constructor() {
		if (this.isBrowser) {
			this.canWebShare = typeof navigator?.canShare === 'function' &&
				navigator.canShare({
					title: "Work Together Perfectly! - May 17, 2024 - Dartegnian.com",
					text: "Check out Dartegnian's journal entry for May 17, 2024!",
					url: "https://dartegnian.com/calendar/2024/05/17",
				});
		}

		effect(() => {
			if (!this.isBrowser) return;
			const state = this.modalState();
			if (state && this.isStandalone()) {
				untracked(() => {
					if (state === 'opening' && this.isBrowser) {
						this.setStyles({ opacity: '0', transition: 'none', transform: 'translate(0px, 0px) scale(1)' });
						requestAnimationFrame(() => {
							this.applyFlipOrigin();
							void this.el.nativeElement.offsetHeight;
							this.setStyles({
								transform: 'translate(0px, 0px) scale(1)',
								opacity: '1',
								transition: 'transform 0.5s cubic-bezier(0.2, 0, 0, 1), opacity 0.5s cubic-bezier(0.2, 0, 0, 1)'
							});
						});
					} else if (state === 'closing' && this.isBrowser) {
						this.applyFlipOrigin();
						this.setStyles({
							opacity: '0',
							transition: 'transform 0.5s cubic-bezier(0.2, 0, 0, 1), opacity 0.5s cubic-bezier(0.2, 0, 0, 1)'
						});
					}
				});
			}
		});

		effect(() => {
			if (!this.isBrowser) return;
			const data = this.dayData();
			if (data) {
				untracked(() => {
					this.calendarEntry = data;
					this.spotifyImage.set(null);
					const currentDate = new Date(Date.UTC(Number(this.year()), this.month(), this.day() + 1));
					this.activeDayName = currentDate.toLocaleDateString('en-US', { weekday: 'short' });
					void this.loadSpotifyImageAndPrefetchAdjacent(this.calendarEntry);
				});
			}
		});
	}



	ngOnDestroy() {
		this.destroy$.next(); // Complete the destroy$ subject, ensuring all ongoing subscriptions are terminated
		this.destroy$.complete();
	}

	private albumArtKey(artist: string, album: string): string {
		return `${artist}::${album}`.toLowerCase();
	}

	private prefetchImage(url: string): void {
		if (!this.isBrowser) return;
		try {
			const img = new Image();
			img.decoding = 'async';
			img.loading = 'eager';
			img.src = url;
		} catch {
			// ignore
		}
	}

	private async getAlbumArtCached(artist: string, album: string): Promise<string | null> {
		const key = this.albumArtKey(artist, album);
		if (this.albumArtCache.has(key)) {
			const cached = this.albumArtCache.get(key)!;
			return cached || null;
		}
		if (this.albumArtInFlight.has(key)) {
			return this.albumArtInFlight.get(key)!;
		}

		const albumArt$ = this.albumArtService.getAlbumArt(artist, album).pipe(
			takeUntil(this.destroy$)
		);

		const promise = lastValueFrom(albumArt$).catch(() => null);
		this.albumArtInFlight.set(key, promise);

		const url = await promise;
		this.albumArtInFlight.delete(key);

		if (url) {
			this.albumArtCache.set(key, url);
			this.prefetchImage(url);
			return url;
		}

		this.albumArtCache.set(key, '');
		return null;
	}

	private getAdjacentEntry(direction: -1 | 1): IPixelMapDay | null {
		try {
			const months = this.calendarDataService.adjustFebruaryDays(this.year());
			const yearData = this.calendarDataService.fetchCalendarData(this.year());

			let m = this.month();
			let d = this.day();
			let safety = 0;

			while (safety++ < 500) {
				d += direction;

				if (d < 0) {
					m -= 1;
					if (m < 0) return null;
					d = months[m].days - 1;
				} else if (d >= months[m].days) {
					m += 1;
					if (m >= months.length) return null;
					d = 0;
				}

				const entry = yearData[m]?.[d];
				if (entry?.status) return entry;
			}
		} catch {
			// ignore
		}
		return null;
	}

	private async loadSpotifyImageAndPrefetchAdjacent(entry: IPixelMapDay): Promise<void> {
		const spotify = entry.spotify;
		if (spotify) {
			const url = await this.getAlbumArtCached(spotify.artist, spotify.album);
			this.spotifyImage.set(url);
		}

		// Prefetch adjacent entries in background
		const prev = this.getAdjacentEntry(-1);
		const next = this.getAdjacentEntry(1);

		if (prev?.spotify) void this.getAlbumArtCached(prev.spotify.artist, prev.spotify.album);
		if (next?.spotify) void this.getAlbumArtCached(next.spotify.artist, next.spotify.album);
	}

	async navigate(nav: "prev" | "next") {
		this.scrollToTop();

		if (!this.isStandalone()) {
			this.router.navigate(
				nav === "prev" ? this.prevLink() : this.nextLink()
			);
		} else {
			this.emitButtonAction.emit(nav);
		}
	}

	scrollToTop(): void {
		const el =
			this.modal()?.nativeElement ??
			(document.getElementById('calendar-entry-modal') as HTMLElement | null);
		if (!el) return;
		requestAnimationFrame(() => {
			try {
				el.scrollIntoView({ behavior: 'smooth' });
			} catch {
				// ignore if element disappeared mid-nav
			}
		});
	}

	copyLinkToClipboard(): void {
		const m = String(this.month() + 1).padStart(2, "0");
		const d = String(this.day() + 1).padStart(2, "0");
		const currentUrl = window.location.origin + `/calendar/${this.year()}/${m}/${d}`;

		navigator.clipboard.writeText(currentUrl).then(() => {
			// console.log('Link copied to clipboard!');
			// You can show a message or toast here to notify the user
			this.copyLinkText = "Link copied!";
			setTimeout(() => this.copyLinkText = "Copy link", 2000);
		}).catch(err => {
			console.error('Failed to copy link: ', err);
			this.copyLinkText = "Copy failed!";
		});
	}

	async webShareEntry(): Promise<void> {
		const m = String(this.month() + 1).padStart(2, "0");
		const d = String(this.day() + 1).padStart(2, "0");
		const url = window.location.origin + `/calendar/${this.year()}/${m}/${d}`;

		const currentDate = `${this.calendarMonths[this.month()].name} ${this.day() + 1}, ${this.year()}`;
		const date = new Date(Date.UTC(Number(this.year()), this.month(), this.day() + 1));
		const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
		const title = this.dayData().title
			? this.dayData().title + " - " + currentDate + " - Dartegnian.com"
			: currentDate + " - Dartegnian.com";

		const shareData = {
			title,
			text: `Here's a link to Dartegnian's journal entry for ${dayName}, ${currentDate}.`,
			url,
		};

		try {
			await navigator.share(shareData);
		} catch (err) {
			console.error(err);
			throw new Error(`${err}`);
		}
	}

	// setModalColor() {
	// if (!this.isStandalone()) return;

	// const color = themeColors[this.dayData().status];

	// // Wait until after Angular has stamped out the <article id="calendar-entry-modal">
	// setTimeout(() => {
	// 	this.accent.setM3SimpleColorAndTarget(
	// 		color,
	// 		'calendar-entry-modal'
	// 	);
	// }, 0);
	// }

	closeButton() {
		this.closeButtonAction.emit("close");
	}

	shuffleButton() {
		this.shuffleButtonAction.emit("shuffle");
	}

	@HostListener('document:keydown.ArrowLeft', ['$event'])
	navigatePrevEntry(event: Event) {
		if (!this.isBrowser || this.extension() === 'shuffle') return;
		event.preventDefault();
		event.stopPropagation();
		this.navigate("prev");
	}

	@HostListener('document:keydown.ArrowRight', ['$event'])
	navigateNextEntry(event: Event) {
		if (!this.isBrowser || this.extension() === 'shuffle') return;
		event.preventDefault();
		event.stopPropagation();
		this.navigate("next");
	}
}
